import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import {
  type CredentialOptionsResponse,
  type EligibleCredential,
  getCredentialOptions,
} from 'lib/api/getCredentialOptions';
import { Maybe, MaybeUndef } from 'types/UtilityTypes';

// API EligibleCredential.level values that map to a fixed UI label
export enum CredentialLoginLevel {
  UserGroup = 'user-group',
  Service = 'service',
}

const LEVEL_LABELS: Record<CredentialLoginLevel, string> = {
  [CredentialLoginLevel.UserGroup]: 'Portal Team',
  [CredentialLoginLevel.Service]: 'Service account',
};

const isCredentialLoginLevel = (level: string): level is CredentialLoginLevel =>
  (Object.values(CredentialLoginLevel) as readonly string[]).includes(level);

/**
 * Labels for each saved portal login: {@link EligibleCredential.username} from the credentials
 * options API when present; otherwise the Outlook inbox address for user-level creds; then neutral labels.
 */
export function getCredentialDisplayLabel(
  credential: EligibleCredential,
  userEmailOrUsername?: MaybeUndef<string>
): string {
  const fromCredential = credential.username?.trim();

  if (fromCredential) {
    return fromCredential;
  }

  if (credential.level === 'user') {
    const trimmed = userEmailOrUsername?.trim();
    if (trimmed) return trimmed;
    return 'Login';
  }

  return isCredentialLoginLevel(credential.level)
    ? LEVEL_LABELS[credential.level]
    : credential.level;
}

export interface UseCredentialOptionsArgs {
  /**
   * When set, skips GET /integrations/credentials/options for this integration.
   * - `null`: parent is still loading a batched fetch (show loading, no network).
   * - `CredentialOptionsResponse`: apply this payload (e.g. from batch).
   * - `undefined`: fetch as usual (default when omitted).
   */
  cachedCredentialOptions?: MaybeUndef<CredentialOptionsResponse>;
}

interface UseCredentialOptionsReturn {
  credentials: EligibleCredential[];
  /**
   * `integrations.id` from the top-level credentials options response (same for all logins in the list).
   */
  integrationId: Maybe<number>;
  selectedCredentialId: Maybe<number>;
  recommendedCredentialId: Maybe<number>;
  setSelectedCredentialId: (id: number) => void;
  isLoading: boolean;
  /**
   * True when the API's `defaultCredentialId` matched an eligible row and was used;
   * false when the first eligible row was used as a fallback.
   */
  isDefaultRecommended: boolean;
}

/**
 * Resolves the login to pre-select. When `defaultCredentialId` is present on the
 * `CredentialOptionsResponse` and matches a row in `eligibleCredentials`, uses it (recommend.go);
 * otherwise uses the first row (the API returns credentials already sorted in recommendation order).
 */
export function pickDefaultCredentialIds(response: CredentialOptionsResponse): {
  // Initial selection; typically matches `recommended` until the user changes the dropdown.
  selected: Maybe<number>;
  // Same as the effective default (API id when valid, else first eligible) for the AI hint.
  recommended: Maybe<number>;
  // True when `defaultCredentialId` from the API matched an eligible row and was used.
  isDefaultRecommended: boolean;
} {
  const { eligibleCredentials, defaultCredentialId } = response;

  if (eligibleCredentials.length === 0) {
    return {
      selected: null,
      recommended: null,
      isDefaultRecommended: false,
    };
  }

  const idSet = new Set(eligibleCredentials.map((c) => c.credentialId));
  const fromApi =
    defaultCredentialId > 0 && idSet.has(defaultCredentialId)
      ? defaultCredentialId
      : null;

  const fallbackFirst = eligibleCredentials[0]!.credentialId;
  const recommended = fromApi ?? fallbackFirst;

  return {
    selected: recommended,
    recommended,
    isDefaultRecommended: fromApi != null,
  };
}

type CredentialOptionsState = {
  credentials: EligibleCredential[];
  integrationId: Maybe<number>;
  selectedCredentialId: Maybe<number>;
  recommendedCredentialId: Maybe<number>;
  isLoading: boolean;
  isDefaultRecommended: boolean;
};

const emptyState: CredentialOptionsState = {
  credentials: [],
  integrationId: null,
  selectedCredentialId: null,
  recommendedCredentialId: null,
  isLoading: false,
  isDefaultRecommended: false,
};

function stateFromResponse(
  response: CredentialOptionsResponse
): Pick<
  CredentialOptionsState,
  | 'credentials'
  | 'integrationId'
  | 'selectedCredentialId'
  | 'recommendedCredentialId'
  | 'isDefaultRecommended'
> {
  const { integrationId, eligibleCredentials } = response;
  const { selected, recommended, isDefaultRecommended } =
    pickDefaultCredentialIds(response);

  return {
    credentials: eligibleCredentials,
    integrationId:
      integrationId != null && integrationId > 0 ? integrationId : null,
    selectedCredentialId: selected,
    recommendedCredentialId: recommended,
    isDefaultRecommended,
  };
}

/**
 * Cached GET /credentials/options (submit-bid batch path).
 */
function getInitialStateFromOptions(
  integrationName: Maybe<string>,
  cached: MaybeUndef<CredentialOptionsResponse>
): CredentialOptionsState {
  if (!integrationName) {
    return { ...emptyState };
  }

  if (cached === null) {
    return { ...emptyState, isLoading: true };
  }

  if (cached !== undefined) {
    return {
      ...stateFromResponse(cached),
      isLoading: false,
    };
  }

  return { ...emptyState };
}

export function useCredentialOptions(
  integrationName: Maybe<string>,
  tmsCustomerId?: string,
  customerName?: string,
  options?: UseCredentialOptionsArgs
): UseCredentialOptionsReturn {
  const cachedCredentialOptions = options?.cachedCredentialOptions;

  const [state, setState] = useState<CredentialOptionsState>(() =>
    getInitialStateFromOptions(integrationName, cachedCredentialOptions)
  );
  const requestIdRef = useRef(0);

  const {
    credentials,
    integrationId,
    selectedCredentialId,
    recommendedCredentialId,
    isLoading,
    isDefaultRecommended,
  } = state;

  const setSelectedCredentialId = useCallback((id: number) => {
    setState((s) => ({ ...s, selectedCredentialId: id }));
  }, []);

  const fetchCredentials = useCallback(async () => {
    if (!integrationName) {
      setState({ ...emptyState });
      return;
    }

    const requestId = ++requestIdRef.current;
    setState((s) => ({ ...s, isLoading: true }));

    const result = await getCredentialOptions({
      integrationName,
      tmsCustomerId,
      customerName,
    });

    if (requestId !== requestIdRef.current) {
      return;
    }

    if (result.isOk()) {
      setState({
        ...stateFromResponse(result.value),
        isLoading: false,
      });
    } else {
      setState({ ...emptyState, isLoading: false });
    }
  }, [integrationName, tmsCustomerId, customerName]);

  useLayoutEffect(() => {
    if (!integrationName) {
      setState({ ...emptyState });
      return;
    }

    if (cachedCredentialOptions === null) {
      setState({ ...emptyState, isLoading: true });
      return;
    }

    if (cachedCredentialOptions !== undefined) {
      setState({
        ...stateFromResponse(cachedCredentialOptions),
        isLoading: false,
      });
      return;
    }
  }, [integrationName, cachedCredentialOptions]);

  useEffect(() => {
    if (!integrationName) {
      return;
    }
    if (cachedCredentialOptions === undefined) {
      void fetchCredentials();
    }
  }, [integrationName, cachedCredentialOptions, fetchCredentials]);

  return {
    credentials,
    integrationId,
    selectedCredentialId,
    recommendedCredentialId,
    setSelectedCredentialId,
    isLoading,
    isDefaultRecommended,
  };
}
